// shared/config/theme.js
// Centralized Design System — the Daily MANIFEST
// Rebrand 2025: "Timeless Atmospheres" — Warm Linen / Sand / Sage palette

// ==================== COLORS ====================
export const colors = {
  // Primary accent — Warm Sand / Terracotta (replaces hot pink)
  dream: {
    50:  '#FAF8F5',
    100: '#F4EFE8',
    200: '#E8DDD0',
    300: '#D6C4AC',
    400: '#C0A485',
    500: '#A8856A',   // main accent
    600: '#8B6A50',
    700: '#6E523D',
    800: '#52392B',
    900: '#35241A',
  },

  // Secondary accent — Muted Sage / Stone Green (replaces purple)
  lavender: {
    50:  '#F4F6F4',
    100: '#E5EAE5',
    200: '#CDD7CD',
    300: '#AEBFAE',
    400: '#8EA38E',
    500: '#6F876F',   // muted sage
    600: '#566A56',
    700: '#404F40',
    800: '#2D382D',
    900: '#1C221C',
  },

  // Warm blush — softened for linen feel (replaces vivid blush)
  blush: {
    50:  '#FAF7F4',
    100: '#F4EDE5',
    200: '#E8D9C8',
    300: '#D6BFA3',
    400: '#BFA080',
    500: '#A8856A',   // mirrors dream-500
    600: '#8B6A50',
    700: '#6E523D',
    800: '#52392B',
    900: '#35241A',
  },

  // Pearl neutrals — warm linen tones
  pearl: {
    50:  '#FDFCFA',   // near-white warm
    100: '#F9F7F3',   // page background (linen)
    200: '#F4F0EA',   // card surface
    300: '#EDE7DC',   // subtle border
    400: '#DDD4C5',   // divider
    500: '#C4B9A8',   // placeholder / muted
    600: '#9C9080',   // secondary text (~60%)
    700: '#6B6058',   // body text (warm mid)
    800: '#403830',   // dark body
    900: '#2B2420',   // near-black (warm-shifted; use instead of pure black)
  },

  // Charcoal text scale (new — for explicit heading/body usage)
  charcoal: {
    50:  '#F5F4F3',
    100: '#E8E6E4',
    200: '#D0CDC9',
    300: '#B0AAA4',
    400: '#8A837B',
    500: '#665E57',
    600: '#524B44',
    700: '#3D3830',
    800: '#2F2A24',
    900: '#2B2420',   // = #2B2B2B equivalent, warm-shifted
  },

  // Legacy / semantic — kept for backward compat
  gray: {
    50:  '#FAFAFA',
    100: '#F5F5F5',
    200: '#E5E5E5',
    300: '#D4D4D4',
    400: '#A3A3A3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626',
    900: '#171717',
  },

  // Kept for any inline references that haven't migrated yet
  pink:   { 500: '#A8856A', 600: '#8B6A50' },
  purple: { 500: '#6F876F', 600: '#566A56' },
  green:  { 500: '#22c55e', 600: '#16a34a' },
  red:    { 500: '#ef4444', 600: '#dc2626' },
};

// ==================== GRADIENTS ====================
export const gradients = {
  // Page background — flat warm linen (NO gradient — brief spec)
  background:    'from-pearl-100 to-pearl-100',
  backgroundAlt: 'from-pearl-50 to-pearl-100',

  // Card surfaces
  card:          'from-white to-pearl-100',
  cardHover:     'from-pearl-50 to-pearl-200',

  // Soft tint for sections / quote cards
  primary:       'from-dream-100 to-pearl-200',
  primaryHover:  'from-dream-200 to-pearl-300',

  // CTA buttons — warm sand gradient
  button:        'from-dream-500 to-dream-600',
  buttonHover:   'from-dream-600 to-dream-700',

  // Accent decorative use only (e.g. icon backgrounds)
  warm:          'from-dream-100 via-pearl-200 to-lavender-100',
  subtle:        'from-pearl-100 to-dream-100',

  // Status
  success:       'from-green-500 to-emerald-600',
  warning:       'from-dream-400 to-dream-500',
};

// ==================== SPACING ====================
export const spacing = {
  xs:   '0.5rem',
  sm:   '0.75rem',
  md:   '1rem',
  lg:   '1.5rem',
  xl:   '2rem',
  '2xl':'3rem',
  '3xl':'4rem',
};

// ==================== TYPOGRAPHY ====================
export const typography = {
  fontFamily: {
    // Canela first (if licensed + self-hosted), Playfair Display as fallback
    serif:   '"Canela", "Playfair Display", Georgia, serif',
    // Inter — use weight 400–500 only per brief
    sans:    '"Inter", ui-sans-serif, system-ui, sans-serif',
    display: '"Canela", "Playfair Display", Georgia, serif',
  },
  fontSize: {
    xs:   '0.75rem',
    sm:   '0.875rem',
    base: '1rem',
    lg:   '1.125rem',
    xl:   '1.25rem',
    '2xl':'1.5rem',
    '3xl':'1.875rem',
    '4xl':'2.25rem',
    '5xl':'3rem',
  },
  fontWeight: {
    // Brief: body/UI at 400–500 only; headings at 400 (Canela looks best light)
    light:    '300',
    normal:   '400',
    medium:   '500',
    semibold: '600',
    bold:     '700',
  },
  lineHeight: {
    tight:   '1.2',
    snug:    '1.35',
    body:    '1.6',
    relaxed: '1.65',   // brief spec for body text
  },
  letterSpacing: {
    tight:   '-0.015em',  // headings
    normal:  '0',
    button:  '0.015em',   // brief: +1.5% for buttons
    label:   '0.08em',    // uppercase labels
    wide:    '0.12em',    // category pills / tracking text
  },
};

// ==================== BORDER RADIUS ====================
export const borderRadius = {
  sm:   '0.5rem',
  md:   '0.75rem',
  lg:   '1rem',
  xl:   '1.25rem',
  '2xl':'1.5rem',
  '3xl':'1.75rem',   // tiles: 18–22px sweet spot
  '4xl':'2rem',
  full: '9999px',
};

// ==================== SHADOWS ====================
// Brief spec: 0 8px 30px rgba(0,0,0,0.06)
// No coloured glow shadows — they cheapen the look
export const shadows = {
  sm:      '0 2px 8px rgba(0,0,0,0.04)',
  md:      '0 4px 16px rgba(0,0,0,0.05)',
  lg:      '0 8px 30px rgba(0,0,0,0.06)',   // brief tile spec
  xl:      '0 12px 40px rgba(0,0,0,0.07)',
  '2xl':   '0 20px 50px rgba(0,0,0,0.09)',
  hover:   '0 16px 48px rgba(0,0,0,0.10)',  // hover lift
  // Named keys used by existing code — remapped to neutral
  dream:   '0 8px 30px rgba(0,0,0,0.06)',
  lavender:'0 8px 30px rgba(0,0,0,0.04)',
  glow:    '0 0 24px rgba(168,133,106,0.15)',   // very subtle warm glow only
  soft:    '0 2px 12px rgba(0,0,0,0.04)',
  card:    '0 8px 30px rgba(0,0,0,0.06)',
};

// ==================== TRANSITIONS ====================
export const transitions = {
  fast:   '150ms',
  normal: '300ms',
  slow:   '500ms',
};

// ==================== BREAKPOINTS ====================
export const breakpoints = {
  sm:   '640px',
  md:   '768px',
  lg:   '1024px',
  xl:   '1280px',
  '2xl':'1536px',
};

// ==================== Z-INDEX ====================
export const zIndex = {
  dropdown:     10,
  sticky:       20,
  modal:        50,
  tooltip:      60,
  notification: 70,
};

// ==================== CARD STYLES ====================
// Brief: no hard borders | radius 18–22px | shadow 0 8px 30px rgba(0,0,0,0.06)
export const cardStyles = {
  default: {
    background: 'bg-white',
    border:     'border-0',                 // no hard borders
    shadow:     'shadow-card',              // maps to 0 8px 30px rgba(0,0,0,0.06)
    rounded:    'rounded-[20px]',           // 20px — within 18–22px brief spec
    padding:    'p-5',
  },
  soft: {
    background: 'bg-pearl-100',
    border:     'border-0',
    shadow:     'shadow-card',
    rounded:    'rounded-[20px]',
    padding:    'p-5',
  },
  glass: {
    background: 'bg-white/70 backdrop-blur-md',
    border:     'border border-pearl-300/50',
    shadow:     'shadow-card',
    rounded:    'rounded-[20px]',
    padding:    'p-5',
  },
  // Subtle bordered — for inset or nested cards only
  outlined: {
    background: 'bg-pearl-50',
    border:     'border border-pearl-300',
    shadow:     'shadow-sm',
    rounded:    'rounded-[16px]',
    padding:    'p-4',
  },
};

// ==================== BUTTON STYLES ====================
// Brief: Inter 500, letter-spacing +1.5%, pill shape
export const buttonStyles = {
  primary: {
    base:       'bg-gradient-to-r from-dream-500 to-dream-600 text-white font-medium',
    hover:      'hover:from-dream-600 hover:to-dream-700',
    active:     'active:scale-[0.97]',
    transition: 'transition-all duration-200',
    rounded:    'rounded-full',
    shadow:     'shadow-md hover:shadow-lg',
    tracking:   'tracking-button',          // +0.015em per brief
  },
  secondary: {
    base:       'bg-white text-pearl-800 font-medium border border-pearl-300',
    hover:      'hover:bg-pearl-100 hover:border-pearl-400',
    active:     'active:scale-[0.97]',
    transition: 'transition-all duration-200',
    rounded:    'rounded-full',
    shadow:     'shadow-sm hover:shadow-md',
    tracking:   'tracking-button',
  },
  soft: {
    base:       'bg-dream-100 text-dream-700 font-medium',
    hover:      'hover:bg-dream-200',
    active:     'active:scale-[0.97]',
    transition: 'transition-all duration-200',
    rounded:    'rounded-full',
    shadow:     'shadow-none',
    tracking:   'tracking-button',
  },
  // Ghost — for secondary actions on dark overlays (journey cards)
  ghost: {
    base:       'bg-white/15 text-white font-medium border border-white/30 backdrop-blur-sm',
    hover:      'hover:bg-white/25',
    active:     'active:scale-[0.97]',
    transition: 'transition-all duration-200',
    rounded:    'rounded-full',
    shadow:     'shadow-none',
    tracking:   'tracking-button',
  },
};

// ==================== AVATAR STYLES ====================
export const avatarStyles = {
  small:   'w-8 h-8',
  medium:  'w-12 h-12',
  large:   'w-16 h-16',
  xlarge:  'w-24 h-24',
  rounded: 'rounded-full',
  border:  'border-2 border-white',
  shadow:  'shadow-md',
};
